import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { applyPatchToState } from "./applyPatchToState";
import type { EntitySaveCoordinator } from "./EntitySaveCoordinator";
import { hasPath } from "./pathUtils";
import { coordinatorRegistry } from "./registry";
import type {
  FieldError,
  SyncStatus,
  UpdateFieldOptions,
} from "./types";

const NOOP_UNSUBSCRIBE = (): void => undefined;
const noopSubscribe = (): (() => void) => NOOP_UNSUBSCRIBE;
const stableEmptyState = Object.freeze({}) as Record<string, unknown>;

interface UseEditorResult<TState, TFields extends Record<string, unknown>> {
  state: TState;
  updateField: (fields: Partial<TFields>, opts?: UpdateFieldOptions) => void;
  entityStatus: SyncStatus;
  /** Accepts either a top-level key of TFields or a dotted path into a nested
   *  field (e.g., "publicContext.eFormsNoticeData.tenderValidityMonths"). */
  fieldStatus: (field: string) => SyncStatus;
  fieldError: (field: string) => FieldError | undefined;
}

/**
 * Reads optimistic state and provides updateField for form components.
 * Requires an active coordinator for the given entityId (via useCreateEditor).
 * Throws if no coordinator is registered.
 *
 * All hooks are called unconditionally; the missing-coordinator throw is
 * raised after every hook has run for the current render so React's hook
 * order stays consistent across renders. Skipping hooks between calls (as a
 * conditional throw earlier in the body does) corrupts React's fiber tree
 * and produces insertBefore/removeChild commit-phase storms when StrictMode's
 * dev double-mount briefly drops the coordinator between cleanup and re-setup.
 *
 * updateField accepts an optional { debounceMs } parameter. When set, the field
 * update is held in the hook layer for the specified duration before being
 * enqueued to the coordinator atomically as a single patch. Optimistic state
 * reflects the value immediately regardless of debounce.
 *
 * One shared timer per hook instance — successive debounced calls reset it and
 * coalesce into the next firing. This is essential for compound DTOs like
 * MoneyDto ({ value, currency }) where the BFF rejects partial bodies; splitting
 * leaves across separate enqueues would let one half ship while the in-flight
 * save was still resolving, producing a partial PUT.
 *
 * An immediate updateField (delay <= 0) mid-debounce flushes any pending
 * debounced state in the same enqueue — preserving the same atomic contract
 * regardless of overlap between the immediate call and the pending paths.
 */
export function useEditor<
  TState extends Record<string, unknown>,
  TFields extends Record<string, unknown> = Partial<TState>,
>(entityId: string): UseEditorResult<TState, TFields> {
  const coordinator = useSyncExternalStore(
    coordinatorRegistry.subscribe,
    useCallback(() => coordinatorRegistry.get(entityId), [entityId]),
  ) as EntitySaveCoordinator<TState, TFields> | undefined;

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncingFields = useRef<Partial<TFields>>({} as Partial<TFields>);
  const [, setDebounceTick] = useState(0);

  // On coordinator change (entityId change) or unmount: cancel the debounce
  // timer and flush any pending debounced values to the coordinator so they
  // aren't silently dropped. The coordinator will attempt to save them
  // (fire-and-forget — the save promise completes independently).
  useEffect(() => {
    return () => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      const pending = debouncingFields.current;
      debouncingFields.current = {} as Partial<TFields>;
      if (coordinator && Object.keys(pending).length > 0) {
        coordinator.enqueue(pending);
      }
    };
  }, [coordinator]);

  // Subscribe to coordinator state unconditionally. When no coordinator is
  // registered (transient during a StrictMode dev double-mount, or during
  // Vite HMR), fall back to a stable no-op subscribe and a frozen empty
  // snapshot so the hook count stays the same across renders. The
  // unconditional-hook discipline avoids the hooks-order mismatch that
  // previously corrupted React's fiber tree mid commit (insertBefore/
  // removeChild storms on Vite full-reload).
  const { subscribe, getSnapshot } = useMemo(() => {
    if (coordinator) {
      return { subscribe: coordinator.subscribe, getSnapshot: coordinator.getOptimisticState };
    }
    return { subscribe: noopSubscribe, getSnapshot: () => stableEmptyState };
  }, [coordinator]);
  const coordinatorState = useSyncExternalStore(subscribe, getSnapshot) as TState;

  const state =
    Object.keys(debouncingFields.current).length > 0
      ? applyPatchToState(coordinatorState, debouncingFields.current)
      : coordinatorState;

  const entityStatus: SyncStatus =
    debounceTimer.current !== null ? "pending" : coordinator?.getEntityStatus() ?? "saved";

  const updateField = useCallback(
    (fields: Partial<TFields>, opts?: UpdateFieldOptions) => {
      if (!coordinator) return;
      const delay = opts?.debounceMs ?? 0;

      if (delay <= 0) {
        if (Object.keys(debouncingFields.current).length > 0) {
          // Flush the pending debounced batch alongside the immediate fields
          // as one enqueue. Splitting would defeat the single-shared-timer
          // atomicity contract: a partial-overlap immediate write would otherwise
          // ship its own fields now and leave non-overlapping pending paths to
          // ship on the next timer fire — exactly the split this rewrite
          // exists to prevent. applyPatchToState handles overrides at any depth
          // (object-over-leaf, null-over-subtree, primitive-over-subtree) so the
          // immediate fields cleanly win wherever they overlap the pending batch.
          if (debounceTimer.current !== null) {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = null;
          }
          const merged = applyPatchToState(
            debouncingFields.current as Record<string, unknown>,
            fields as Record<string, unknown>,
          ) as Partial<TFields>;
          debouncingFields.current = {} as Partial<TFields>;
          coordinator.enqueue(merged);
          setDebounceTick((t) => t + 1);
          return;
        }
        coordinator.enqueue(fields);
        return;
      }

      debouncingFields.current = applyPatchToState(
        debouncingFields.current,
        fields,
      ) as Partial<TFields>;

      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        const pending = debouncingFields.current;
        debouncingFields.current = {} as Partial<TFields>;
        if (Object.keys(pending).length > 0) {
          coordinator.enqueue(pending);
        }
        // Drives entityStatus's pending → idle render. `debounceTimer.current`
        // is a ref, so flipping it doesn't re-render on its own.
        setDebounceTick((t) => t + 1);
      }, delay);

      setDebounceTick((t) => t + 1);
    },
    [coordinator],
  );

  const fieldStatus = useCallback(
    (field: string) => {
      if (hasPath(debouncingFields.current as Partial<Record<string, unknown>>, field)) {
        return "pending" as const;
      }
      return coordinator?.getFieldStatus(field) ?? "saved";
    },
    [coordinator],
  );

  const fieldError = useCallback(
    (field: string) => coordinator?.getFieldError(field),
    [coordinator],
  );

  if (!coordinator) {
    throw new Error(
      `useEditor: no active coordinator for entityId "${entityId}". ` +
        `Did you forget to call useCreateEditor in a parent component?`,
    );
  }

  return { state, updateField, entityStatus, fieldStatus, fieldError };
}
